<script lang="ts">
import { computed, defineComponent, unref, warn } from 'vue';

import {
  makeLinkProps,
  makeColorProps,
  makeTextProps,
  useLink,
  useColor,
  makeThemeProps,
  makeIconProps,
  provideTheme,
  useIcon
} from '../../composable';
import { CdsIcon } from '../CdsIcon';

import type { PropType } from 'vue';

export const componentName = 'CdsTag';
export default defineComponent({
  name: componentName,
  components: { CdsIcon },
  props: {
    removable: {
      type: Boolean as PropType<boolean>,
      default: false,
      description: 'Удаляемый тег'
    },
    ...makeLinkProps(),
    ...makeColorProps(),
    ...makeTextProps(),
    ...makeIconProps(),
    ...makeThemeProps()
  },
  emits: {
    /**
     * Событие удаления
     *
     * @event remove
     */
    remove: (event: MouseEvent | KeyboardEvent) => true
  },
  setup(props, { slots, emit }) {
    const { themeClasses } = provideTheme(props);
    const { colorInline } = useColor(props, componentName);
    const { isLink, tag, href, to } = useLink(props, emit);
    const { hasAppend, hasPrepend } = useIcon(props, slots, componentName);

    const hasText = computed(() => !!slots.text || !!props.text);

    if (!unref(hasText) && !slots.default) {
      warn(`[CDS] 'text' is a required prop for ${componentName}`);
    }

    const handleRemove = (event: MouseEvent): void => {
      emit('remove', event);
    };

    const tagClasses = computed(() => ({
      'cds-tag--interactive': unref(isLink)
    }));

    const tagStyles = computed(() => ({
      '--cds-tag-background': unref(colorInline).background,
      '--cds-tag-color': unref(colorInline).color,
      '--cds-tag-hover': unref(colorInline).hover
    }));

    const classes = {
      tagClasses,
      themeClasses
    };

    return {
      tag: unref(isLink) ? tag : 'span',
      href,
      to,
      tagStyles,
      hasText,
      hasPrepend,
      hasAppend,
      ...classes,
      handleRemove
    };
  }
});
</script>

<template>
  <component :is="tag" :to="to" :href="href" class="cds-tag" :style="tagStyles" :class="[tagClasses, themeClasses]">
    <span v-if="hasPrepend">
      <!--
        @slot Слот для иконки в начале
        @type CdsIcon | svg
      -->
      <cds-icon v-if="prependIcon" :name="prependIcon" />
      <slot name="prepend" />
    </span>

    <span v-if="hasText" tabindex="-1">
      <span>{{ text }}</span>
      <!--
        @slot Слот для текста
        @type string
      -->
      <slot name="text" />
    </span>

    <!--
       @slot Слот по умолчанию
       @type string
     -->
    <slot />

    <span v-if="hasAppend">
      <!--
        @slot Слот для иконки в конце
        @type CdsIcon | svg
      -->
      <cds-icon v-if="appendIcon" :name="appendIcon" />
      <slot name="append" />
    </span>

    <cds-icon
      v-if="removable"
      class="cds-tag__remove"
      name="remove"
      tabindex="0"
      @click="handleRemove"
      @keydown.enter.prevent="handleRemove"
    />
  </component>
</template>

<style lang="scss">
@import './CdsTag';
</style>
